// React imports
import { useCallback, useEffect, useRef, useState } from 'react';

// React Query import
import { useQuery } from '@tanstack/react-query';

// OpenLayers imports
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { unByKey } from 'ol/Observable';

// API and context imports
import { getUnrotatedGrid, getDetections } from '@utils/api';
import { useProject } from '@contexts/Project.context';
import { IMAGE_MODE } from '@utils/constants';

// Utility imports
import { deleteLayerByCustomId, getLayerByCustomId } from '@utils/map/helpers';
import { createTooltip } from '@utils/map/tooltip.overlay';
import {
	styleFunction,
	fetchFeaturesImageData,
	createFeature,
	animateFeature,
	getImagePoint,
} from '@utils/map/layer/singleImage';

// Component imports
import LayerSettings from '@routes/userRoutes/projects/singleProject/components/sidebars/sidebarElements/LayerSettings';
import { SingleImageOverlayTooltip } from './OverlayTooltips';
import SelectedSingleImageLayer from './SelectedSingleImageLayer';
import MapLoader from '@routes/userRoutes/projects/singleProject/components/MapLoader';
import SingleImageFootprinsLayer from './SingleImageFootprintsLayers';

const { SINGLE_IMAGE } = IMAGE_MODE;

const SingleImagePointsLayer = ({ layerId = 'singleImagePointsLayer' }) => {
	const {
		mapObject,
		defaultProjection,
		project,
		pickedTask,
		colorScheme,
		toolBarVisible,
		activeFilename,
		interactionsDisabled,
		selectedSingleImage,
		savedMapExtent,
		dispatch,
	} = useProject();
	const { uuid: projectUuid, image_mode: imageMode } = project ?? {};
	const { model_uuid: modelUuid } = pickedTask ?? {};

	const adding = useRef(false);
	const pointermovePopupKey = useRef(null);

	const tooltipRef = useRef(null);
	const tooltipContainerRef = useRef(null);
	const [tooltipContent, setTooltipContent] = useState(null);

	const [singleImagePointsLayer, setSingleImagePointsLayer] = useState(null);
	const [loading, setLoading] = useState(true);
	const [imagesNotReady, setImagesNotReady] = useState(false);
	const loadedImages = useRef(0);
	const loadedImagesContainer = useRef(null);

	// Fetch grid data
	const {
		data: gridData,
		isError: dataLoadError,
		error,
	} = useQuery({
		queryKey: ['fetchUnrotatedGrid', projectUuid],
		queryFn: () => getUnrotatedGrid(projectUuid),
		enabled: !!projectUuid && imageMode == SINGLE_IMAGE,
		refetchOnWindowFocus: false,
		retry: false,
	});
	const gridFeatures = gridData?.features;

	// Fetch detections data
	const { data: detectionsData } = useQuery({
		queryKey: ['fetchDetectionsNormalized', projectUuid, modelUuid],
		queryFn: () => getDetections(projectUuid, modelUuid, 'normalized'),
		enabled: !!projectUuid && !!modelUuid && !!gridFeatures,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const updateLoadedImages = () => {
		// Increment the count of loaded images
		loadedImages.current += 1;

		// Check if the loaded images container DOM element is available
		if (loadedImagesContainer.current) {
			// Update the text content of the DOM element with the current count of loaded images
			loadedImagesContainer.current.textContent = loadedImages.current;
		}
	};

	const updateImagesNotReady = () => {
		setImagesNotReady(true);
		setLoading(false);
	};

	// Fetch features image data
	const { data: featuresData, isLoading: featuresLoading } = useQuery({
		queryKey: ['single_photo_features', projectUuid],
		queryFn: ({ signal }) =>
			fetchFeaturesImageData({
				gridFeatures,
				projectUuid: projectUuid,
				signal,
				updateLoadedImages,
				updateImagesNotReady,
			}),
		enabled: !!gridFeatures,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchInterval: 3000000, // Set refetch interval to 50 minutes (3000000 milliseconds)
	});

	// Function to update the single photo layer
	const updateSinglePhotoLayer = useCallback(async () => {
		// Check if the layer is currently being added or if features are loading
		if (!adding.current && !featuresLoading && featuresData?.length) {
			// Set the adding flag to true to prevent concurrent additions
			adding.current = true;

			// Check if the single image layer already exists. If it does, add it to state and exit early
			const existingSingleImageLayer = getLayerByCustomId(mapObject, layerId);
			if (existingSingleImageLayer) {
				setSingleImagePointsLayer(existingSingleImageLayer);

				adding.current = false;
				setLoading(false);
				return;
			}

			// Create a new vector source for the single image layer
			const singleImageSource = new VectorSource({
				features: [],
			});

			// Create a new vector layer with the source and additional properties
			const layer = new VectorLayer({
				source: singleImageSource,
				visible: true,
				defaultProjection: defaultProjection,
				zIndex: 24,
				properties: {
					customLayerId: layerId,
				},
				style: feature => styleFunction({ feature, mapObject }),
			});

			// Add the new layer to the map
			mapObject.addLayer(layer);

			// Map the features data to create new features for the layer
			const singleImageFeatures = featuresData.map(data => {
				const feature = createFeature(data);
				if (!feature) return null;
				singleImageSource.addFeature(feature);

				return feature;
			});

			const isValidExtent = extent => {
				if (!extent) return false;

				return !extent.some(coord => coord === null || !isFinite(coord) || isNaN(coord));
			};

			const fitViewToExtent = (extent, maxZoom) => {
				if (!isValidExtent(extent))
					return console.error(
						'Single image features were made but has no valid extent',
						`ProjectId: ${projectUuid}`,
						`ModelId: ${modelUuid}`
					);

				const view = mapObject.getView();
				view.fit(extent, { maxZoom, padding: [0, 0, 202, 0] }); // 202 is imageSlider height in px
			};

			// Warn if no features.
			if (!singleImageFeatures?.length)
				console.warn('No features were added to the single image layer.');
			else {
				const sourceExtent = singleImageSource.getExtent();

				// If no saved extent, fit view to the features.
				if (!savedMapExtent) fitViewToExtent(sourceExtent, 17);
				else {
					// Else fit to saved extent and clear saved extent.
					fitViewToExtent(savedMapExtent, 28);
					dispatch({ type: 'setSavedMapExtent', payload: null });
				}
			}

			// Set the new layer to the state
			setSingleImagePointsLayer(layer);
			// Reset the adding flag and loading state
			adding.current = false;
			setLoading(false);
		}
	}, [featuresLoading]);

	// Function to update detections on the single image layer
	const updateDetections = useCallback(() => {
		// Early return if singleImageLayer is not defined
		if (!singleImagePointsLayer) return;

		// Get all features from the single image layer
		const singleImageFeatures = singleImagePointsLayer?.getSource()?.getFeatures();

		// Remove any existing detections from the features
		singleImageFeatures?.forEach(feature => {
			feature.set('detections', null);
		});

		// Check if detectionsData contains features
		if (detectionsData?.features) {
			// Iterate over each feature in singleImageFeatures
			singleImageFeatures.forEach(feature => {
				// Get the filename associated with the current feature
				const filename = feature.get('name');

				// Filter detections that match the current feature's filename
				const matchingDetections = detectionsData.features.filter(
					detection => detection.properties.tile_name === filename
				);

				// If there are any matching detections, process them
				if (matchingDetections.length > 0) {
					// Reduce detections to accumulate class information
					const classes = matchingDetections.reduce((acc, detection) => {
						const { classname: name, classid: id } = detection.properties;

						// Check if the class already exists in the accumulator
						const existingClass = acc.find(
							item => item.name === name && item.id === id
						);

						// If the class exists, increment its count
						// else class does not exist, add it to the accumulator
						if (existingClass) existingClass.count += 1;
						else acc.push({ name, id, count: 1 });

						return acc;
					}, []);

					// Set the detections and classes information on the feature
					feature.set('detections', {
						features: matchingDetections,
						classes,
					});
				}
			});
		}

		// Dispatch actions to update the state with the new features
		dispatch({
			type: 'setSingleImageFeatures',
			payload: singleImageFeatures,
		});
		dispatch({
			type: 'setFilteredImages',
			payload: singleImageFeatures,
		});
	}, [detectionsData, singleImagePointsLayer]);

	useEffect(() => {
		// Check if there was an error loading the data
		if (dataLoadError) {
			// Log a warning message to the console
			console.warn('Could not fetch single photo', error);

			// If the map object and single image layer exist, delete the layer
			if (mapObject && singleImagePointsLayer) {
				// Delete the single image layer from the map using its custom ID
				deleteLayerByCustomId(mapObject, layerId);

				// Set the single image layer state to null
				setSingleImagePointsLayer(null);
			}

			setLoading(false);
			alert('Something went wrong. Please try again later.');

			// Exit the effect early since there was an error
			return;
		}

		// If there was no error, update the single photo layer
		updateSinglePhotoLayer();
	}, [updateSinglePhotoLayer, dataLoadError]);

	// Function to create tooltip content for a feature
	const createTooltipContent = feature => {
		// to show imagePoint is clickable
		mapObject.getTargetElement().style.cursor = 'pointer';

		// Check if the toolbar is visible
		if (!toolBarVisible) return;

		// Retrieve the thumbnail URL from the feature
		const thumb = feature.get('thumbnail');
		if (!thumb) return; // Exit if no thumbnail is available

		// Get the coordinates of the feature's geometry
		const coordinates = feature.getGeometry().getCoordinates();

		// Set the tooltip position to the feature's coordinates
		tooltipRef.current.setPosition(coordinates);

		// Update the tooltip content with the thumbnail URL and detection classes
		setTooltipContent({
			imageUrl: thumb,
			detectionClasses: feature.get('detections')?.classes || [], // Default to an empty array if no detections
		});
	};

	// CRITICAL: MAY NEED 'center' and 'outline' in acceptedFeatureTypes

	// Handler for pointermove event
	const activeFilenameRef = useRef(null);
	const lastActiveFilenameRef = useRef(null);
	const acceptedFeatureTypes = ['center', 'outline', 'bounding_box', 'imagePoint'];
	const detectionFeatureTypes = ['center', 'outline', 'bounding_box'];
	const pointerMoveHandler = useCallback(
		event => {
			/*
			//	FIX: selectedSingleImage should probably be removed and points/polygons should probably not be visible while having a selectedSingleImage
			//	
			//	This should be fixed when branch 134-merge-polygon-and-point-layer-with-webgl is merged with main and should probably be done in DetectionResultsLayer.jsx
			//	Example: detectionLayer?.setVisible(true/false)
			*/

			// Ensure the map object is available and interactions are not disabled
			if (!mapObject || interactionsDisabled || selectedSingleImage) return;

			const featuresAtPixel = mapObject.getFeaturesAtPixel(event.pixel);
			const feature = featuresAtPixel.find(feature =>
				acceptedFeatureTypes.includes(feature?.get('featureType'))
			);
			const featureType = feature?.get('featureType');

			if (featureType == 'imagePoint') createTooltipContent(feature);
			else tooltipRef.current.setPosition(undefined);

			if (!acceptedFeatureTypes.includes(featureType))
				mapObject.getTargetElement().style.cursor = 'auto';

			if (detectionFeatureTypes.includes(featureType)) {
				const filename = feature.get('tile_name');

				if (activeFilenameRef.current != filename) activeFilenameRef.current = filename;
			} else activeFilenameRef.current = null;

			// if the active filename has changed
			if (activeFilenameRef.current != lastActiveFilenameRef.current) {
				dispatch({
					type: 'setActiveFilename',
					payload: activeFilenameRef.current,
				});

				if (activeFilenameRef.current) {
					const feature = getImagePoint(
						singleImagePointsLayer,
						activeFilenameRef.current
					);
					animateFeature({ mapObject, feature });
				}

				if (lastActiveFilenameRef.current) {
					const feature = getImagePoint(
						singleImagePointsLayer,
						lastActiveFilenameRef.current
					);
					animateFeature({
						mapObject,
						feature,
						makeActive: false,
						onComplete: () => feature.setStyle(null),
					});
					lastActiveFilenameRef.current = null;
				}

				lastActiveFilenameRef.current = activeFilenameRef.current;
			}
		},
		[
			tooltipRef,
			colorScheme,
			activeFilename,
			interactionsDisabled,
			selectedSingleImage,
			singleImagePointsLayer,
			toolBarVisible,
		]
	);

	useEffect(() => {
		// Skip if no map or layer
		if (!singleImagePointsLayer || !mapObject) return;

		// Create Tooltip only if it doesn't exist yet
		if (!tooltipRef.current)
			tooltipRef.current = createTooltip({
				mapRef: mapObject,
				tooltipRef: tooltipContainerRef.current,
				id: 'single-image-tooltip',
			});

		// Remove any existing event listener
		if (pointermovePopupKey.current) unByKey(pointermovePopupKey.current);

		// Add the new event listener with the current pointerMoveHandler
		const pointermoveKey = mapObject.on('pointermove', pointerMoveHandler);
		pointermovePopupKey.current = pointermoveKey;

		// Cleanup function
		return () => {
			if (pointermovePopupKey.current) {
				unByKey(pointermovePopupKey.current);
				pointermovePopupKey.current = null;
			}
		};
	}, [mapObject, singleImagePointsLayer, interactionsDisabled, pointerMoveHandler]);

	useEffect(() => {
		updateDetections();
	}, [updateDetections]);

	useEffect(() => {
		return () => {
			// Clean up on unmount

			// If the layer is currently being added, skip cleanup
			if (adding.current) return;

			// Remove the pointer move popup event listener if it exists
			if (pointermovePopupKey.current) {
				unByKey(pointermovePopupKey.current);
				pointermovePopupKey.current = null;
			}

			// Delete the single image layer from the map
			deleteLayerByCustomId(mapObject, layerId);
			setSingleImagePointsLayer(null);

			// Remove the tooltip overlay from the map if it exists
			if (mapObject && tooltipRef.current) {
				mapObject.removeOverlay(tooltipRef.current);
				tooltipRef.current = null;
			}

			// Reset the loaded images count
			loadedImages.current = 0;
			setImagesNotReady(false);
		};
	}, []);

	return (
		<li>
			<LayerSettings
				id={layerId}
				label="Images"
				defaultState={true}
				onCheck={() => singleImagePointsLayer?.setVisible(true)}
				onUncheck={() => singleImagePointsLayer?.setVisible(false)}
				disabled={!singleImagePointsLayer}
			/>

			{loading && (
				<MapLoader>
					{gridFeatures?.length ? (
						<p style={{ minWidth: '250px' }} className="mt-4 mb-0">
							Loading
							<br />
							<span ref={loadedImagesContainer}>{loadedImages.current}</span> /{' '}
							{gridFeatures.length} images
						</p>
					) : (
						<p className="mt-4 mb-0">Loading images...</p>
					)}
				</MapLoader>
			)}

			{imagesNotReady && (
				<MapLoader showLoader={false}>
					<p className="mb-0">The images are not ready. Please check back later.</p>
				</MapLoader>
			)}

			{singleImagePointsLayer && (
				<>
					<SelectedSingleImageLayer
						singleImageLayer={singleImagePointsLayer}
						singleImageLayerTooltipRef={tooltipRef.current}
					/>
					<div ref={tooltipContainerRef}>
						<SingleImageOverlayTooltip {...tooltipContent} colorScheme={colorScheme} />
					</div>

					<ul>
						<SingleImageFootprinsLayer />
					</ul>
				</>
			)}
		</li>
	);
};
export default SingleImagePointsLayer;
